// Stop league invites to an address, from the link at the foot of an invite
// (PLAN-2026-10-28-by-email §3.2, NOTIFICATIONS.md §5).
//
// `/no-invites?k=<key>`. The key is the proof, so nobody signs in: the person
// may never have made an account. The page says what it stops and stops it
// with one press — never on opening, because mail scanners open every link in
// a message, as the email stop's page says. A mail provider's own button stops
// it without this page at all (RFC 8058).
//
// IT IS FOR GOOD, AND SAYS SO, before the press: there is no turning invites
// back on from here, and an account's own email is not touched.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { InvitesStopView } from '@fsp/domain';
import { get, messageOf, send } from '../data/api';
import { BRAND } from '../branding';
import { useTier } from '../shell/useTier';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { Text } from '../kit/ui';

type Stage = 'reading' | 'ask' | 'stopped' | 'off' | 'failed';

export function NoInvitesScreen() {
  const tier = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  const [key] = useState(() => new URLSearchParams(location.search).get('k') ?? '');
  const [stage, setStage] = useState<Stage>('reading');
  const [problem, setProblem] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const path = `/email/invites-stop?k=${encodeURIComponent(key)}`;

  useEffect(() => {
    void (async () => {
      const reply = await get(path, InvitesStopView);
      if (reply.status === 'ok') {
        setStage(reply.data.stopped ? 'off' : 'ask');
        return;
      }
      setProblem(
        messageOf(
          reply,
          'That link did not work.',
          'Could not reach the site. Check your connection.',
        ),
      );
      setStage('failed');
    })();
  }, [path]);

  const stop = async () => {
    if (pending) return;
    setPending(true);
    const reply = await send('POST', path, {}, InvitesStopView);
    setPending(false);
    if (reply.status === 'ok') {
      setStage('stopped');
      return;
    }
    setProblem(
      messageOf(
        reply,
        'That did not work. Try again.',
        'Could not reach the site. Check your connection.',
      ),
    );
  };

  const steps: Record<Stage, WizardStep> = {
    reading: {
      id: 'reading',
      title: 'Stop league invites',
      content: (
        <Text tone="secondary" size="sm" block>
          One moment.
        </Text>
      ),
      doneLabel: 'Reading…',
    },
    ask: {
      id: 'ask',
      title: 'Stop league invites?',
      hint: `No league on ${BRAND.name} will invite the address this invite went to again. This can't be undone from here.`,
      content: (
        <Text tone="secondary" size="sm" block>
          If somebody still wants you in their league, they can send you the link themselves. Email
          about an account of your own is not affected.
        </Text>
      ),
      error: problem,
      doneLabel: pending ? 'Stopping…' : 'Stop league invites',
    },
    stopped: {
      id: 'stopped',
      title: 'Stopped',
      hint: `No league on ${BRAND.name} will invite this address again.`,
      content: null,
      doneLabel: 'Done',
    },
    off: {
      id: 'off',
      title: 'League invites are stopped',
      hint: `This address gets no league invites from ${BRAND.name} already.`,
      content: null,
      doneLabel: 'Done',
    },
    failed: {
      id: 'failed',
      title: 'That link will not work',
      hint: problem ?? 'That link did not work.',
      content: null,
      doneLabel: 'Done',
    },
  };

  const home = () => navigate('/', { replace: true });
  return (
    <div className="fsp-frame" data-tier={tier}>
      <Wizard
        title="League invites"
        steps={[steps[stage]]}
        index={0}
        onIndex={() => undefined}
        onDone={() => {
          if (stage === 'ask') void stop();
          else if (stage !== 'reading') home();
        }}
        onCancel={stage === 'ask' ? home : undefined}
      />
    </div>
  );
}
